import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Check,
  Link2,
  Loader2,
  Swords,
  X,
  UserMinus,
  UserPlus,
  UserCheck,
} from 'lucide-react';

import { Button } from '@/components/common/Button';
import { useHaptics } from '@/hooks/useHaptics';
import { useSfidaDiPiattaforma } from '@/hooks/useSfidaDiPiattaforma';
import {
  useGetUserPresenceQuery,
  useAcceptFriendshipMutation,
  useRemoveFriendshipMutation,
  useFollowUserMutation,
  useUnfollowUserMutation,
} from '@/services/socialApi';
import {
  useCreateChallengeMutation,
  useGetMyLinkQuery,
} from '@/services/qduelsApi';
import { classifyQduelsError, isNotLinkedError } from '@/services/qduelsErrors';
import { cn } from '@/lib/cn';
import { liveMatchDaMostrare } from '@/lib/qsocials/liveMatch';
import type { PresenceStatus } from '@/types/social';
import type { DuelFormat, DuelGameMode } from '@/types/qduels';

import { ChallengeOptionsSheet } from './ChallengeOptionsSheet';
import { GuardaLive } from './GuardaLive';
import { PresenceBadge } from './PresenceBadge';
import { SfidaSheetDaCatalogo } from './SfidaSheet';
import { UserAvatar } from './UserAvatar';
import { OSCURA } from '@/lib/v3';

type ItemMode = 'friend' | 'follow' | 'follower' | 'incoming' | 'outgoing';

interface FriendListItemProps {
  userId: string;
  /**
   * Synced profile display name (Wave 2). When absent we fall back to the
   * legacy `nickname`, then to a generic "QLASH player" label — we never
   * surface the raw Logto sub to users.
   */
  displayName?: string | null;
  /** Synced profile avatar URL (Wave 2). */
  avatarUrl?: string | null;
  /** Optional nickname; used only when no synced displayName is present. */
  nickname?: string;
  mode: ItemMode;
  /** Hide the presence badge when not relevant (e.g. pending request row). */
  hidePresence?: boolean;
  /**
   * Solo per `mode="follower"` (punto 8, ridisegno Amici 2026-09-28): letto
   * dal chiamante sulla lista Seguiti GIA' caricata (`Friends.tsx`), non da
   * una query per riga — revisione indipendente #484, punto 4. Oltre il
   * tetto di paginazione della lista Seguiti una riga non ancora caricata
   * arriva `undefined`/`false` e mostra il bottone Segui: una riga di
   * sicuro NON ancora seguita mostrerebbe la stessa cosa, quindi non è
   * un falso positivo — è un «non lo so ancora» che si comporta come «no».
   */
  alreadyFollowing?: boolean;
  className?: string;
}

/**
 * Single row in any of the social lists (Friends / Following / Followers /
 * Requests). The footer actions adapt to `mode`:
 *
 *   friend    → Remove
 *   follow    → Unfollow
 *   follower  → Follow back (if iFollow=false) / nothing otherwise
 *   incoming  → Accept · Decline
 *   outgoing  → Cancel (delete the friendship row)
 *
 * Presence dot is fetched per-row via /presence/:userId. The query is cached
 * by RTK Query so opening multiple sub-tabs of the same user only hits the
 * API once.
 */
export function FriendListItem({
  userId,
  displayName,
  avatarUrl,
  nickname,
  mode,
  hidePresence,
  alreadyFollowing: alreadyFollowingProp,
  className,
}: FriendListItemProps) {
  const { t } = useTranslation('general');
  const haptics = useHaptics();
  const navigate = useNavigate();

  const presenceQuery = useGetUserPresenceQuery(userId, { skip: hidePresence });
  const status: PresenceStatus = presenceQuery.data?.presence.status ?? 'offline';
  // «Sta giocando» (contratto QSocials r38): campo A PARTE dallo status. Assente = null = niente; una partita
  // senza orologio conta solo se l'amico e' anche online. V. `lib/qsocials/liveMatch.ts`.
  const live = hidePresence
    ? null
    : liveMatchDaMostrare(presenceQuery.data?.presence.liveMatch, presenceQuery.data?.presence.status);

  const [acceptFriendship, accepting] = useAcceptFriendshipMutation();
  const [removeFriendship, removing] = useRemoveFriendshipMutation();
  const [followUser, following] = useFollowUserMutation();
  const [unfollowUser, unfollowing] = useUnfollowUserMutation();

  // Punto 8 del ridisegno Amici (2026-09-28): su Follower, chi gia' segui
  // mostra «Segui già» al posto del bottone Segui — oggi compariva sempre.
  // Il dato arriva dal chiamante (v. `alreadyFollowing` sopra): niente query
  // per riga qui dentro.
  const alreadyFollowing = mode === 'follower' && Boolean(alreadyFollowingProp);

  const busy =
    accepting.isLoading ||
    removing.isLoading ||
    following.isLoading ||
    unfollowing.isLoading;

  const onAccept = () => {
    haptics.notification('success');
    acceptFriendship({ friendUserId: userId });
  };
  const onRemove = () => {
    haptics.impact('medium');
    removeFriendship({ friendUserId: userId });
  };
  const onFollow = () => {
    haptics.impact('light');
    followUser({ followedUserId: userId });
  };
  const onUnfollow = () => {
    haptics.impact('light');
    unfollowUser({ followedUserId: userId });
  };
  const openProfile = () => {
    haptics.selection();
    navigate(`/profile/${encodeURIComponent(userId)}`);
  };

  // QDuels 1v1 challenge (friends list only). `defenderLogtoId` is the friend's
  // canonical QLASH user id — the same value QSocials stores as `friendUserId`
  // (Logto sub), which this row receives as `userId`. Defaults: bo3 / REGULAR.
  //
  // Gate: a challenge fails with `challenger_not_linked` if the CURRENT user
  // hasn't linked a Clash Royale account. We probe qduels' link status up front
  // (`GET /v1/players/me`, 404 = not linked) so the Sfida button can route the
  // user to /duels/link instead of firing a request that's guaranteed to 4xx.
  // The query is RTK-Query-cached + deduped, so rendering N friend rows still
  // only hits the endpoint once. Skipped on non-friend rows (no Sfida there).
  const isFriendRow = mode === 'friend';
  // I-207: dietro `useSfidaDiPiattaforma` (staging, ruolo beta) il bottone apre la sfida di
  // piattaforma, che non e' di Clash Royale: niente sonda del collegamento CR, niente «collega
  // prima». A flag spento tutto resta com'era.
  const sfidaPiattaforma = useSfidaDiPiattaforma();
  const [sfidaAperta, setSfidaAperta] = useState(false);
  const myLinkQuery = useGetMyLinkQuery(undefined, { skip: !isFriendRow || sfidaPiattaforma });
  // Treat ONLY a 404 as "not linked". A transient auth/network/5xx error must
  // not mask the button into the link affordance — let the challenge attempt
  // surface the real error instead. While the probe is still loading we
  // optimistically allow the challenge; if the server then returns
  // `challenger_not_linked`, the catch below refetches the probe so the button
  // flips to the "link first" affordance.
  const challengerNotLinked = !sfidaPiattaforma && isNotLinkedError(myLinkQuery.error);

  const [createChallenge, creatingChallenge] = useCreateChallengeMutation();
  const [challengeSent, setChallengeSent] = useState(false);
  const [challengeMessage, setChallengeMessage] = useState<string | null>(null);
  const [challengeSheetOpen, setChallengeSheetOpen] = useState(false);
  const challengeBusy = creatingChallenge.isLoading;

  const goLinkCr = () => {
    haptics.impact('light');
    navigate('/duels/link');
  };

  // Tap on "Sfida": platform challenge (flag + beta, I-207) → `SfidaSheet`, where the game is
  // chosen; otherwise not-linked → link flow, else the QDuels options sheet so the challenger
  // picks the game mode + best-of before sending.
  const onChallengeTap = () => {
    if (challengeBusy || challengeSent) return;
    if (sfidaPiattaforma) {
      haptics.impact('light');
      setSfidaAperta(true);
      return;
    }
    if (challengerNotLinked) {
      goLinkCr();
      return;
    }
    haptics.impact('light');
    setChallengeMessage(null);
    setChallengeSheetOpen(true);
  };

  // Fired by ChallengeOptionsSheet with the chosen mode + series.
  const submitChallenge = async ({
    format,
    gameMode,
  }: {
    format: DuelFormat;
    gameMode: DuelGameMode;
  }) => {
    if (challengeBusy || challengeSent) return;
    haptics.impact('medium');
    setChallengeMessage(null);
    try {
      await createChallenge({
        defenderLogtoId: userId,
        format,
        gameMode,
        createdVia: 'telegram',
      }).unwrap();
      haptics.notification('success');
      setChallengeSent(true);
      setChallengeSheetOpen(false);
      setChallengeMessage(
        t('social.challenge_sent', { defaultValue: 'Challenge sent!' }),
      );
    } catch (err) {
      haptics.notification('error');
      setChallengeSheetOpen(false);
      const kind = classifyQduelsError(err);
      // Server confirms we're not linked (the proactive probe may not have
      // resolved yet, or was a transient error). Refetch the link status so the
      // button flips to the "link first" affordance on the next render.
      if (kind === 'challenger_not_linked' && isFriendRow) {
        void myLinkQuery.refetch();
      }
      setChallengeMessage(
        kind === 'defender_not_linked'
          ? t('social.challenge_defender_not_linked', {
              defaultValue:
                "This friend hasn't linked their Clash Royale account yet.",
            })
          : kind === 'challenger_not_linked'
            ? t('social.challenge_challenger_not_linked', {
                defaultValue:
                  'Link your Clash Royale account first to send challenges.',
              })
            : kind === 'cannot_challenge_self'
              ? t('social.challenge_cannot_self', {
                  defaultValue: "You can't challenge yourself.",
                })
              : kind === 'auth'
                ? t('social.challenge_auth_error', {
                    defaultValue:
                      'Could not authenticate for challenges. Try signing in again.',
                  })
                : t('social.challenge_error', {
                    defaultValue: 'Could not send the challenge. Try again.',
                  }),
      );
    }
  };

  // Resolution order: synced profile name → legacy nickname → generic label.
  // We never display the raw Logto sub.
  const resolvedName =
    displayName ||
    nickname ||
    t('social.unknown_user', { defaultValue: 'QLASH player' });

  return (
    <li
      className={cn(
        'flex flex-col gap-2 rounded-xl bg-bg-secondary p-3',
        className,
      )}
    >
      <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={openProfile}
        className="flex min-w-0 flex-1 items-center gap-3 text-left transition-opacity active:opacity-70"
      >
        <div className="relative shrink-0">
          <UserAvatar
            userId={userId}
            label={resolvedName}
            avatarUrl={avatarUrl}
            size="md"
          />
          {!hidePresence && (
            <span className="absolute -bottom-0.5 -right-0.5 rounded-full bg-bg-secondary p-0.5">
              <PresenceBadge status={status} />
            </span>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-fg" {...OSCURA}>{resolvedName}</p>
          {live && (
            <p className="truncate text-[11px] font-medium text-brand" data-testid="sta-giocando">
              {t('social.row_playing', { defaultValue: 'Playing now' })}
            </p>
          )}
          {!live && !hidePresence && status !== 'offline' && (
            <p className="truncate text-[11px] text-fg-muted">
              {status === 'in_tournament'
                ? t('social.row_in_tournament', { defaultValue: 'In tournament' })
                : status === 'watching'
                  ? t('social.row_watching', { defaultValue: 'Watching live' })
                  : t('social.row_online', { defaultValue: 'Online' })}
            </p>
          )}
          {mode === 'outgoing' && (
            <p className="text-[11px] text-fg-muted">
              {t('social.request_pending', { defaultValue: 'Request pending' })}
            </p>
          )}
        </div>
      </button>

      <div className="flex shrink-0 items-center gap-1.5">
        {live && <GuardaLive live={live} />}
        {mode === 'incoming' && (
          <>
            <RowButton
              variant="primary"
              onClick={onAccept}
              disabled={busy}
              aria-label={t('social.accept', { defaultValue: 'Accept' })}
            >
              <Check className="h-4 w-4" />
            </RowButton>
            <RowButton
              variant="secondary"
              onClick={onRemove}
              disabled={busy}
              aria-label={t('social.decline', { defaultValue: 'Decline' })}
            >
              <X className="h-4 w-4" />
            </RowButton>
          </>
        )}
        {mode === 'outgoing' && (
          <RowButton
            variant="secondary"
            onClick={onRemove}
            disabled={busy}
            aria-label={t('social.cancel_request', { defaultValue: 'Cancel' })}
          >
            <X className="h-4 w-4" />
          </RowButton>
        )}
        {mode === 'friend' && (
          <RowButton
            variant={challengerNotLinked ? 'secondary' : 'primary'}
            onClick={onChallengeTap}
            disabled={busy || challengeBusy || challengeSent}
            aria-label={
              challengerNotLinked
                ? t('social.challenge_link_cta', {
                    defaultValue: 'Link your Clash Royale account to challenge',
                  })
                : t('social.challenge', { defaultValue: 'Challenge' })
            }
          >
            {challengeBusy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : challengeSent ? (
              <Check className="h-4 w-4" />
            ) : challengerNotLinked ? (
              <Link2 className="h-4 w-4" />
            ) : (
              <Swords className="h-4 w-4" />
            )}
          </RowButton>
        )}
        {mode === 'friend' && (
          <RowButton
            variant="secondary"
            onClick={onRemove}
            disabled={busy}
            aria-label={t('social.remove_friend', { defaultValue: 'Remove' })}
          >
            <UserMinus className="h-4 w-4" />
          </RowButton>
        )}
        {mode === 'follow' && (
          <RowButton
            variant="secondary"
            onClick={onUnfollow}
            disabled={busy}
            aria-label={t('social.unfollow', { defaultValue: 'Unfollow' })}
          >
            <UserCheck className="h-4 w-4" />
          </RowButton>
        )}
        {mode === 'follower' && alreadyFollowing && (
          <span className="px-1 text-[11px] font-semibold text-fg-muted">
            {t('social.following', { defaultValue: 'Following' })}
          </span>
        )}
        {mode === 'follower' && !alreadyFollowing && (
          <RowButton
            variant="primary"
            onClick={onFollow}
            disabled={busy}
            aria-label={t('social.follow_back', { defaultValue: 'Follow' })}
          >
            <UserPlus className="h-4 w-4" />
          </RowButton>
        )}
      </div>
      </div>

      {/* Challenge (QDuels) feedback line — success ("Sfida inviata!") or a
          mapped error. Only friend rows render the Challenge action, so this
          stays empty for every other mode. When the current user isn't linked
          yet, we show a tappable "link first" affordance instead of an error. */}
      {isFriendRow && challengerNotLinked && !challengeMessage && (
        // `text-[11px] font-normal text-fg-muted underline`: corpo, peso e colore di OGGI sul `link` (PR-2, override nominati).
        <Button
          variant="link"
          onClick={goLinkCr}
          className="self-start text-[11px] font-normal text-fg-muted underline after:h-8"
        >
          {t('social.challenge_link_first', {
            defaultValue: 'Link your Clash Royale account first to challenge',
          })}
        </Button>
      )}
      {challengeMessage && (
        <p
          className={cn(
            'text-[11px]',
            challengeSent ? 'text-success' : 'text-danger',
          )}
          role="status"
        >
          {challengeMessage}
        </p>
      )}

      {challengeSheetOpen && (
        <ChallengeOptionsSheet
          open
          opponentName={resolvedName}
          busy={challengeBusy}
          onSubmit={submitChallenge}
          onClose={() => setChallengeSheetOpen(false)}
        />
      )}
      {sfidaAperta && (
        <SfidaSheetDaCatalogo avversario={resolvedName} amicoId={userId} onClose={() => setSfidaAperta(false)} />
      )}
    </li>
  );
}

function RowButton({
  variant,
  children,
  className,
  ...rest
}: Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'> & {
  'aria-label': string;
  variant: 'primary' | 'secondary';
}) {
  return (
    // Il ramo `primary` (teal pieno) e' un override di pelle sull'icona `neutro` (PR-2): un solo bottone, non un asse.
    // `after:w-9`: due icone da 32 con 6 px fra loro si sovrapporrebbero di 6 px a 44 di larghezza (misurato, PR-2).
    <Button
      variant="icona"
      size={32}
      tono="neutro"
      className={cn(variant === 'primary' && 'bg-brand text-brand-fg', 'after:w-9', className)}
      {...rest}
    >
      {children}
    </Button>
  );
}
